import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Edit3, Clock, User, Calendar, ChevronDown, ChevronUp, Bot, Eye } from 'lucide-react';
import { calcDurationDays, formatDuration, STANDARD_PHASES } from '../../../utils/phaseSequencing';

const AIDraftCard = ({ project, onApprove, onReject, isProcessing }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr)?.toLocaleDateString('en-SG', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const phases = project?.phases || [];

  // Estimated duration: calculated from project start/end dates (replaces cost display)
  const durationDays = calcDurationDays(project?.start_date, project?.end_date);
  const durationLabel = formatDuration(durationDays);

  // If no duration from dates, estimate from phases using STANDARD_PHASES durations
  const estimatedDaysFromPhases = phases?.reduce((sum, phase) => {
    const phaseName = (phase?.phase_name || phase?.required_trade || '')?.toLowerCase();
    const standard = STANDARD_PHASES?.find(
      sp => sp?.name?.toLowerCase() === phaseName || phaseName?.includes(sp?.name?.toLowerCase())
    );
    return sum + (standard?.durationDays ?? 7);
  }, 0);

  const displayDuration = durationLabel || (estimatedDaysFromPhases > 0 ? formatDuration(estimatedDaysFromPhases) : null);

  const isAiImport = project?.source === 'ai_import';
  const hasStartDate = !!project?.start_date;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-2 min-w-0">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mt-0.5">
              <Bot className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground text-sm leading-tight truncate">
                {project?.name}
              </h3>
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                <Clock className="w-3 h-3" />
                {isAiImport ? 'AI Import · Draft' : 'AI Draft · Pending Review'}
              </span>
            </div>
          </div>
        </div>

        {/* Project dates */}
        {hasStartDate ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{formatDate(project?.start_date)} — {formatDate(project?.end_date)}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 mb-2">
            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="italic">No start date set — open to schedule</span>
          </div>
        )}

        {/* Estimated duration */}
        {displayDuration && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
            <Clock className="w-3.5 h-3.5 flex-shrink-0 text-primary" />
            <span>Est. duration: <span className="font-semibold text-foreground">{displayDuration}</span></span>
          </div>
        )}

        {/* Subcon summary */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <User className="w-3.5 h-3.5 flex-shrink-0" />
            <span>{phases?.length} phase{phases?.length !== 1 ? 's' : ''}</span>
          </div>
          {phases?.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
            >
              {expanded ? (
                <>Hide <ChevronUp className="w-3.5 h-3.5" /></>
              ) : (
                <>View phases <ChevronDown className="w-3.5 h-3.5" /></>
              )}
            </button>
          )}
        </div>

        {/* Expanded phases */}
        {expanded && phases?.length > 0 && (
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            {phases?.map((phase, index) => {
              const phaseName = (phase?.phase_name || phase?.required_trade || '')?.toLowerCase();
              const standard = STANDARD_PHASES?.find(
                sp => sp?.name?.toLowerCase() === phaseName || phaseName?.includes(sp?.name?.toLowerCase())
              );
              const phaseDuration = standard?.durationDays ?? 7;

              return (
                <div key={phase?.id} className="flex items-start justify-between gap-2 text-xs">
                  <div className="min-w-0 flex items-start gap-1.5">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center mt-0.5">
                      {index + 1}
                    </span>
                    <div>
                      <span className="font-medium text-foreground capitalize">{phase?.phase_name || phase?.required_trade}</span>
                      <div className="text-muted-foreground mt-0.5 flex items-center gap-2">
                        <span className="flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />
                          Est. {formatDuration(phaseDuration)}
                        </span>
                        {phase?.worker?.full_name ? (
                          <span className="flex items-center gap-0.5">
                            <User className="w-3 h-3" />
                            {phase?.worker?.full_name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-muted-foreground flex-shrink-0">
                    {phase?.planned_start_date ? (
                      <>
                        <div>{formatDate(phase?.planned_start_date)}</div>
                        <div>{formatDate(phase?.planned_end_date)}</div>
                      </>
                    ) : (
                      <span className="text-amber-500 italic text-[10px]">Not scheduled</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="px-4 pb-4 flex items-center gap-2 flex-wrap">
        {/* For AI imports: show "Open" button to go to phase management for scheduling */}
        {isAiImport ? (
          <button
            onClick={() => navigate('/phase-management', { state: { projectId: project?.id, projectName: project?.name } })}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Eye className="w-3.5 h-3.5" />
            {hasStartDate ? 'View & Manage' : 'Open & Schedule'}
          </button>
        ) : (
          <button
            onClick={() => onApprove(project?.id, project?.name)}
            disabled={isProcessing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Approve
          </button>
        )}
        <button
          onClick={() => navigate(`/project-creation`, { state: { draftProjectId: project?.id, draftData: project } })}
          disabled={isProcessing}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-secondary hover:bg-secondary/80 text-secondary-foreground text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Edit3 className="w-3.5 h-3.5" />
          Edit
        </button>
        <button
          onClick={() => onReject(project?.id, project?.name)}
          disabled={isProcessing}
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 hover:bg-destructive/20 text-destructive text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircle className="w-3.5 h-3.5" />
          Reject
        </button>
      </div>
    </div>
  );
};

export default AIDraftCard;
